import React from 'react';
import AppBar from '@mui/material/AppBar';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import {
  AdminNotification,
  getAdminNotifications,
  getAdminNotificationUnreadCount,
  markAdminNotificationRead,
  markAllAdminNotificationsRead,
  normalizeNotification,
} from '../services/notifications';

type TopbarProps = {
  accessToken?: string;
  demoMode?: boolean;
  isSidebarCollapsed?: boolean;
  onCreate?: () => void;
  onLogout?: () => void;
  onSearchChange?: (value: string) => void;
  onToggleSidebar?: () => void;
  role: string;
  searchValue?: string;
  title: string;
};

const WS_BASE_URL = process.env.REACT_APP_WS_URL ?? 'ws://localhost:8000';
const ADMIN_NOTIFICATIONS_WS_URL = `${WS_BASE_URL.replace(/\/$/, '')}/admin/notifications/ws`;

function Topbar({
  accessToken,
  demoMode,
  isSidebarCollapsed,
  onCreate,
  onLogout,
  onSearchChange,
  onToggleSidebar,
  role,
  searchValue = '',
  title,
}: TopbarProps) {
  const [notifications, setNotifications] = React.useState<AdminNotification[]>([]);
  const [isNotificationsOpen, setIsNotificationsOpen] = React.useState(false);
  const [showUnreadOnly, setShowUnreadOnly] = React.useState(false);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const isAdmin = role === 'admin' || role === 'super_admin';

  React.useEffect(() => {
    if (!isAdmin || !accessToken || demoMode) {
      return undefined;
    }

    let socket: WebSocket | undefined;
    let reconnectTimer: number | undefined;
    let isDisposed = false;

    Promise.all([
      getAdminNotifications(),
      getAdminNotificationUnreadCount(),
    ]).then(([initialNotifications, initialUnreadCount]) => {
      if (!isDisposed) {
        setNotifications(initialNotifications);
        setUnreadCount(initialUnreadCount);
      }
    }).catch(() => undefined);

    const connect = () => {
      socket = new WebSocket(`${ADMIN_NOTIFICATIONS_WS_URL}?token=${encodeURIComponent(accessToken)}`);

      socket.onmessage = (event) => {
        const notification = parseSocketNotification(event.data);

        if (!notification) {
          return;
        }

        setNotifications((current) => [notification, ...current].slice(0, 20));
        setUnreadCount((current) => current + 1);
      };

      socket.onclose = () => {
        if (!isDisposed) {
          reconnectTimer = window.setTimeout(connect, 3000);
        }
      };
    };

    connect();

    return () => {
      isDisposed = true;

      if (reconnectTimer) {
        window.clearTimeout(reconnectTimer);
      }

      socket?.close();
    };
  }, [accessToken, demoMode, isAdmin]);

  const toggleNotifications = () => {
    setIsNotificationsOpen((current) => !current);
  };

  const toggleUnreadOnly = async () => {
    const nextUnreadOnly = !showUnreadOnly;
    setShowUnreadOnly(nextUnreadOnly);

    try {
      setNotifications(await getAdminNotifications(nextUnreadOnly));
    } catch {
      setShowUnreadOnly(!nextUnreadOnly);
    }
  };

  const markNotificationRead = async (notificationId: string) => {
    const notification = notifications.find((item) => item.id === notificationId);

    if (!notification || notification.isRead) {
      return;
    }

    await markAdminNotificationRead(notificationId);
    setNotifications((current) => current
      .map((item) => item.id === notificationId ? { ...item, isRead: true } : item)
      .filter((item) => !showUnreadOnly || !item.isRead));
    setUnreadCount((current) => Math.max(0, current - 1));
  };

  const markAllRead = async () => {
    await markAllAdminNotificationsRead();
    setUnreadCount(0);
    setNotifications((current) => showUnreadOnly ? [] : current.map((item) => ({ ...item, isRead: true })));
  };

  return (
    <AppBar className="topbar" color="transparent" component="header" elevation={0} position="static">
      <div className="tb-left">
        {onToggleSidebar ? (
          <IconButton
            className="btn-icon sidebar-toggle"
            aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-pressed={isSidebarCollapsed}
            onClick={onToggleSidebar}
            size="small"
          >
            <MenuRoundedIcon aria-hidden="true" fontSize="medium" />
          </IconButton>
        ) : null}
        <Typography component="h1" variant="h1">{title}</Typography>
        <span className={`role-badge ${role}`}>{role.replace('_', ' ')}</span>
      </div>
      <div className="tb-right">
        <Paper className="search-box" component="label" elevation={0} htmlFor="global-search">
          <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="2" />
            <path d="M16 16l4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <input
            id="global-search"
            placeholder="Search..."
            type="search"
            value={searchValue}
            onChange={(event) => onSearchChange?.(event.target.value)}
          />
        </Paper>
        {role !== 'signed_out' ? (
          <div className="notification-menu">
            <IconButton
              className="btn-icon"
              aria-expanded={isNotificationsOpen}
              aria-label="Notifications"
              onClick={toggleNotifications}
            >
              <Badge badgeContent={isAdmin ? unreadCount : 0} color="error" max={99}>
                <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M6 9a6 6 0 0112 0c0 7 3 7 3 9H3c0-2 3-2 3-9" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path d="M10 21a2 2 0 004 0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </Badge>
            </IconButton>
            {isAdmin && isNotificationsOpen ? (
              <Paper className="notification-panel" component="section" elevation={8} aria-label="Admin notifications">
                <div className="notification-panel-head">
                  <Typography component="h2" variant="h3">Notifications</Typography>
                  <div className="notification-actions">
                    <Button
                      className={`btn btn-sm${showUnreadOnly ? ' btn-primary' : ''}`}
                      onClick={toggleUnreadOnly}
                      size="small"
                      variant={showUnreadOnly ? 'contained' : 'outlined'}
                    >
                      Unread
                    </Button>
                    <Button className="btn btn-sm" disabled={!unreadCount} onClick={markAllRead} size="small" variant="outlined">
                      Mark all read
                    </Button>
                  </div>
                </div>
                <div className="notification-list">
                  {notifications.length ? notifications.map((notification) => (
                    <button
                      className={`notification-item${notification.isRead ? '' : ' unread'}`}
                      key={notification.id}
                      type="button"
                      onClick={() => markNotificationRead(notification.id)}
                    >
                      <strong>{notification.title}</strong>
                      <p>{notification.message}</p>
                      <time>{formatNotificationTime(notification.createdAt)}</time>
                    </button>
                  )) : (
                    <div className="notification-empty">
                      <strong>No notifications yet</strong>
                      <span>New admin alerts will appear here.</span>
                    </div>
                  )}
                </div>
              </Paper>
            ) : null}
          </div>
        ) : null}
        {onCreate ? (
          <Button className="btn btn-primary" onClick={onCreate} variant="contained">
            New
          </Button>
        ) : null}
        {onLogout ? (
          <Button className="btn btn-ghost" onClick={onLogout} variant="text">
            Sign out
          </Button>
        ) : null}
      </div>
    </AppBar>
  );
}

function parseSocketNotification(data: unknown): AdminNotification | null {
  try {
    const payload = typeof data === 'string' ? JSON.parse(data) : data;
    return normalizeNotification(payload);
  } catch {
    return typeof data === 'string' && data
      ? { id: String(Date.now()), title: 'Admin notification', message: data, createdAt: new Date().toISOString(), isRead: false }
      : null;
  }
}

function formatNotificationTime(timestamp: string) {
  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? timestamp : date.toLocaleString();
}

export default Topbar;
